// Constants for inactivity timer (in milliseconds)
const WARNING_TIMEOUT = 1000 * 60 * 8; // 8 minutes
const LOGOUT_TIMEOUT = 1000 * 60 * 10; // 10 minutes (2 minutes after warning)
const WARNING_COUNTDOWN_TIMEOUT = 1000 * 60 * 2; // 2 minutes countdown after warning

type WarningCallback = () => void;
type LogoutCallback = () => void;

class InactivityTracker {
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private warningCallback: WarningCallback | null = null;
  private logoutCallback: LogoutCallback | null = null;
  private isActive = false;
  private isWarningActive = false;

  constructor() {
    this.resetTimers = this.resetTimers.bind(this);
    this.handleInactivityWarning = this.handleInactivityWarning.bind(this);
    this.handleInactivityLogout = this.handleInactivityLogout.bind(this);
    this.continueSession = this.continueSession.bind(this);
  }

  /**
   * Start tracking user activity
   */
  public startTracking(
    warningCallback: WarningCallback,
    logoutCallback: LogoutCallback,
  ): void {
    if (this.isActive) {
      return;
    }

    this.warningCallback = warningCallback;
    this.logoutCallback = logoutCallback;
    this.isActive = true;
    this.isWarningActive = false;

    // Set up event listeners for user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    events.forEach((event) => {
      document.addEventListener(event, this.resetTimers);
    });

    // Initial timer start
    this.resetTimers();
  }

  /**
   * Stop tracking user activity
   */
  public stopTracking(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;
    this.isWarningActive = false;

    // Remove event listeners
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    events.forEach((event) => {
      document.removeEventListener(event, this.resetTimers);
    });

    // Clear timers
    this.clearTimers();
  }

  /**
   * Reset inactivity timers
   */
  private resetTimers(): void {
    // If warning modal is active, don't reset timers on general activity
    // Only explicit continueSession call should reset timers when warning is active
    if (this.isWarningActive) {
      return;
    }

    this.clearTimers();

    // Set new timers
    this.warningTimer = setTimeout(
      this.handleInactivityWarning,
      WARNING_TIMEOUT,
    );
    this.logoutTimer = setTimeout(this.handleInactivityLogout, LOGOUT_TIMEOUT);
  }

  /**
   * Explicitly continue session (called when user clicks "Continue Session" button)
   */
  public continueSession(): void {
    this.isWarningActive = false;
    this.clearTimers();
    // Reset both timers to their full duration
    this.warningTimer = setTimeout(
      this.handleInactivityWarning,
      WARNING_TIMEOUT,
    );
    this.logoutTimer = setTimeout(this.handleInactivityLogout, LOGOUT_TIMEOUT);
  }

  /**
   * Clear all active timers
   */
  private clearTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  /**
   * Handle inactivity warning
   */
  private handleInactivityWarning(): void {
    console.log('Inactivity warning triggered after 8 minutes');
    this.isWarningActive = true;

    // Clear the existing logout timer and set a new one for exactly 2 minutes
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }
    this.logoutTimer = setTimeout(
      this.handleInactivityLogout,
      WARNING_COUNTDOWN_TIMEOUT,
    );

    if (this.warningCallback) {
      this.warningCallback();
    }
  }

  /**
   * Handle inactivity logout
   */
  private handleInactivityLogout(): void {
    console.log('Inactivity logout triggered');
    this.isWarningActive = false;
    // Call the logout callback provided by the provider
    if (this.logoutCallback) {
      this.logoutCallback();
    }

    // Stop tracking since user is now logged out
    this.stopTracking();
  }
}

// Export singleton instance
export const inactivityTracker = new InactivityTracker();
